import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { AuditTrailEntryDto } from '@common/audit-trail/auditTrailTypes';
import { AuditEventCategory, EntityAuditTrailAuthorDto, EntityAuditTrailEntryDto } from 'types/entity-audit-trail';

export interface AuthorsQueryArgs {
    entityId: number;
    categories: AuditEventCategory[];
}
import { portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

type AuditTrailEndpointBuilder = EndpointBuilder<
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
    string,
    'portalAPI'
>;

/**
 * The two endpoints every per-entity trail exposes under one base path: the paged feed and the
 * people appearing in it. Filters travel as plain query params (categories/authorIds/includeSystem
 * via extraParams), not RSQL — the BE endpoints have no filter grammar.
 */
function entityAuditTrailEndpoints(
    builder: AuditTrailEndpointBuilder,
    tag: string,
    basePath: (entityId: string | number) => string
) {
    return {
        entries: accumulatedQueryWithFilters<EntityAuditTrailEntryDto>(builder, basePath, {
            providesTags: [tag],
            dedupeKey: (entry) => entry.entryId,
        }),
        // The people offered mirror the feed the viewer is looking at: an opt-in category adds its
        // authors only while it is selected, so the filter never lists someone who cannot match.
        authors: builder.query<EntityAuditTrailAuthorDto[], AuthorsQueryArgs>({
            query: ({ entityId, categories }) => ({
                url: `${basePath(entityId)}/authors`,
                params: categories.length > 0 ? { categories: categories.join(',') } : undefined,
            }),
            providesTags: [tag],
        }),
    };
}

const APIAuditTrailSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['AuditTrail', 'InvoiceAuditTrail', 'MatterAuditTrail'] })
    .injectEndpoints({
        endpoints: (builder) => {
            const invoice = entityAuditTrailEndpoints(
                builder,
                'InvoiceAuditTrail',
                (invoiceId) => `/v1/invoices/${invoiceId}/audit-trail`
            );
            const matter = entityAuditTrailEndpoints(
                builder,
                'MatterAuditTrail',
                (matterId) => `/v1/matters/${matterId}/audit-trail`
            );
            return {
                getAuditTrailEntries: accumulatedQueryWithFilters<AuditTrailEntryDto>(builder, '/v1/audit-trail', {
                    providesTags: ['AuditTrail'],
                }),
                getAuditTrailEntityTypes: builder.query<string[], void>({
                    query: () => '/v1/audit-trail/entity-types',
                    providesTags: ['AuditTrail'],
                }),
                getInvoiceAuditTrailEntries: invoice.entries,
                getInvoiceAuditTrailAuthors: invoice.authors,
                getMatterAuditTrailEntries: matter.entries,
                getMatterAuditTrailAuthors: matter.authors,
            };
        },
        overrideExisting: false,
    });

export const {
    useLazyGetAuditTrailEntriesQuery,
    useGetAuditTrailEntityTypesQuery,
    useLazyGetInvoiceAuditTrailEntriesQuery,
    useGetInvoiceAuditTrailAuthorsQuery,
    useLazyGetMatterAuditTrailEntriesQuery,
    useGetMatterAuditTrailAuthorsQuery,
} = APIAuditTrailSlice;
